import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { ReactNodeViewRenderer, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { AssetImageNode } from '@/features/notes/AssetImage';
import type { UploadKind } from '@/features/notes/AssetUploadModal';
import { createBacklinksMenu } from '@/features/notes/BacklinksMenu';
import CodeBlockView from '@/features/notes/CodeBlockView';
import { CredentialMentionNode } from '@/features/notes/CredentialMention';
import { createCredentialMenu } from '@/features/notes/CredentialMenu';
import { UserMentionNode } from '@/features/notes/UserMention';
import { createUserMenu } from '@/features/notes/UserMenu';
import { FileAttachmentNode } from '@/features/notes/FileAttachment';
import { NoteMentionNode } from '@/features/notes/NoteMention';
import { NoticeBlockNode } from '@/features/notes/NoticeBlock';
import { SecretInlineNode } from '@/features/notes/SecretInline';
import { ToggleBlockNode } from '@/features/notes/ToggleBlock';
import { createSlashMenu } from '@/features/notes/SlashMenu';
import { cerbesLowlight } from '@/features/notes/editor/lowlight';

/**
 * Instanța Tiptap a editorului de notițe, cu toate extensiile Cerbes.
 * Ține și starea modalului de upload, fiindcă îl deschide meniul slash.
 */
export function useNoteTiptap({
  tenantId,
  noteId,
  isEdit,
  mode,
}: {
  tenantId: string;
  noteId: string | null;
  isEdit: boolean;
  mode: 'view' | 'edit';
}): {
  editor: Editor | null;
  uploadModal: UploadKind | null;
  setUploadModal: Dispatch<SetStateAction<UploadKind | null>>;
} {
  const [uploadModal, setUploadModal] = useState<UploadKind | null>(null);
  const slashMenu = useMemo(
    () => createSlashMenu(isEdit ? (kind) => setUploadModal(kind) : undefined),
    [isEdit],
  );
  const backlinksMenu = useMemo(
    () => createBacklinksMenu(tenantId, noteId),
    [tenantId, noteId],
  );
  const credentialMenu = useMemo(
    () => createCredentialMenu(tenantId),
    [tenantId],
  );
  const userMenu = useMemo(
    () => createUserMenu(tenantId),
    [tenantId],
  );
  const editor = useEditor(
    {
      extensions: [
        // Dezactivăm codeBlock built-in ca să folosim versiunea lowlight
        // cu NodeView custom (header + copy + select limbaj).
        StarterKit.configure({ codeBlock: false }),
        CodeBlockLowlight.extend({
          addNodeView() {
            return ReactNodeViewRenderer(CodeBlockView);
          },
        }).configure({
          lowlight: cerbesLowlight,
          defaultLanguage: 'plaintext',
          HTMLAttributes: { class: 'cerbes-code-block' },
        }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Table.configure({ resizable: true, HTMLAttributes: { class: 'cerbes-table' } }),
        TableRow,
        TableHeader,
        TableCell,
        NoticeBlockNode,
        SecretInlineNode,
        ToggleBlockNode,
        CredentialMentionNode,
        UserMentionNode,
        AssetImageNode,
        FileAttachmentNode,
        NoteMentionNode,
        slashMenu,
        backlinksMenu,
        credentialMenu,
        userMenu,
      ],
      content: '',
      editable: mode === 'edit',
      editorProps: {
        attributes: {
          class:
            // `flow-root` creează BFC ca să cuprindă imaginile cu `float`
            // (align stânga/dreapta). Fără asta, height-ul editor-ului
            // colapsează și footer-ul urcă în fața imaginii.
            'prose prose-sm max-w-none focus:outline-none min-h-[400px] px-4 py-3 [display:flow-root]',
        },
      },
    },
    [noteId],
  );

  useEffect(() => {
    if (editor) editor.setEditable(mode === 'edit');
  }, [editor, mode]);

  return { editor, uploadModal, setUploadModal };
}
